import { Button, Dropdown, Flex, Input, Space, Typography } from "antd";
import { DateSelector } from "../../generic/DateSelector";
import { IoFilter, IoSearch } from "react-icons/io5";
import { debounce } from "../../../utils/debounce";
import { SmsReportQuery } from "../../../hooks/apis/reports.api";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { FaCaretDown } from "react-icons/fa";

export const FiltersPanel = ({
    filters,
    setFilters,
    isFilterOpen,
    setIsFilterOpen,
    clearFilters,
    messagesType,
    setMessagesType,
    onSearchChange,
    onDateChange
}: {
    filters: SmsReportQuery;
    setFilters: React.Dispatch<React.SetStateAction<SmsReportQuery>>;
    handleRefetchReports?: () => void;
    isFilterOpen: boolean;
    setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
    clearFilters: () => void;
    messagesType: "messages" | "bulk_messages";
    setMessagesType: React.Dispatch<React.SetStateAction<"messages" | "bulk_messages">>;
    onSearchChange?: (value: string) => void;
    onDateChange: (date: { start_date: Date; end_date: Date }) => void;
}) => {
    const [date, setDate] = useState<{
        start_date: Date;
        end_date: Date;
    }>({
        start_date: filters.start_date || new Date(dayjs().startOf("day").toISOString()),
        end_date: filters.end_date || new Date(dayjs().endOf("day").toISOString())
    });

    useEffect(() => {
        if (filters.start_date && filters.end_date) {
            const startChanged = dayjs(filters.start_date).valueOf() !== dayjs(date.start_date).valueOf();
            const endChanged = dayjs(filters.end_date).valueOf() !== dayjs(date.end_date).valueOf();
            if (startChanged || endChanged) {
                setDate({
                    start_date: filters.start_date,
                    end_date: filters.end_date
                });
            }
        }
    }, [filters.start_date, filters.end_date]);

    const handleDateSelection = (nextDate: { start_date: Date; end_date: Date }) => {
        setDate(nextDate);
        onDateChange(nextDate);
    };

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                if (onSearchChange) {
                    onSearchChange(value);
                } else {
                    setFilters(prev => ({
                        ...prev,
                        searchInput: value || null
                    }));
                }
            }, 800),
        [onSearchChange, setFilters]
    );

    return (
        <Flex align="center" justify="space-between">
            <div className="project-ant">
                <Flex gap={10}>
                    <DateSelector date={date} setDate={handleDateSelection} />
                    <Dropdown
                        className="date-dropdown-container"
                        menu={{
                            items: [
                                {
                                    key: "messages",
                                    label: "Messages",
                                    onClick: () => {
                                        setMessagesType("messages");
                                    }
                                },
                                {
                                    key: "bulk",
                                    label: "Bulk",
                                    onClick: () => {
                                        setMessagesType("bulk_messages");
                                    }
                                }
                            ]
                        }}
                    >
                        <Button type="primary">
                            {" "}
                            <Space>
                                <Typography.Text style={{ color: "white" }} strong>
                                    {messagesType === "messages" ? "Messages" : "Bulk"}
                                </Typography.Text>
                                <FaCaretDown style={{ marginTop: "4px" }} />
                            </Space>
                        </Button>
                    </Dropdown>

                    <Input
                        className="report-select-filter"
                        style={{ minWidth: "400px", maxWidth: "400px", borderRadius: "15px" }}
                        placeholder="Search by message body"
                        prefix={<IoSearch />}
                        onChange={e => debouncedSearch(e.target.value.trim())}
                        allowClear
                    />
                </Flex>
            </div>
            <Flex gap={10} style={{ marginRight: "10px" }}>
                <Button style={{ width: "fit-content" }} onClick={() => setIsFilterOpen(!isFilterOpen)} icon={<IoFilter />} type="default">
                    {"Filters"}
                </Button>
                {isFilterOpen && (
                    <Button
                        onClick={() => {
                            clearFilters();
                        }}
                        type="default"
                    >
                        Clear
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};
